import { Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { map, catchError, tap, mergeMap } from 'rxjs/operators';
import { UserService } from './open-api/user.service';
import { LocalStorageService } from './utils/local-storage.service';
import { AssetUtilsService } from './utils/asset-utils.service';
import { Logo, LogoResult } from '../models/logo-types';
import { LogoMetadataDto, LogoGetDto } from '../models/logo-dtos';

/**
 * Centralized service for managing user logo operations including:
 * - Fetching logos while managing local storage caching
 * - Uploading logos with cache updates
 * - Deleting logos with cache invalidation
 * - Retrieving logo metadata for conditional requests
 *
 * This service abstracts away caching, local storage, and API interactions that
 * pertains to the organisational logo.
 */
@Injectable({
    providedIn: 'root',
})
export class LogoService {
    constructor(
        private readonly userService: UserService,
        private readonly assetUtilsService: AssetUtilsService,
    ) {}

    /**
     * Fetches the logo for the specified user with cache handling.
     * Checks cache validity against server metadata first,
     * then returns cached logo if fresh or fetches from server if stale.
     *
     * @param userId The ID of the user whose logo to fetch
     * @returns Observable<string | null> emitting a base64 encoded string of the
     *          logo image (), or null if no logo exists
     */
    getLogo(userId: number): Observable<Logo> {
        return this.userService.getReportLogoMetadata().pipe(
            mergeMap((metadata: LogoMetadataDto) => {
                const lastModified = metadata.lastModified;
                // If no lastModified, it means no logo exists on server
                // Cache must be cleared and no logo returned (logo was deleted or doesn't exist)
                if (!lastModified) {
                    this.clearCache(userId);
                    return of({ logo: null, source: 'none' as const });
                }

                const metaDataDate = new Date(lastModified);
                const cachedDate = LocalStorageService.getLogoLastModified(userId);
                const cachedLogo = LocalStorageService.getUserUploadedLogo(userId);

                // If server logo is newer or cache date is missing or no cached logo, fetch fresh logo
                if (!cachedDate || !cachedLogo || metaDataDate > cachedDate) {
                    return this.fetchLogoFromServer(userId, lastModified);
                }

                return of({ logo: cachedLogo, source: 'cache' as const });
            }),
            map((result: LogoResult) => result.logo),
            catchError((error) => {
                throw error;
            }),
        );
    }

    /**
     * Fetches the logo from the server and updates the cache.
     * Assumes metadata has already been validated by the caller.
     *
     * @param userId The user ID
     * @param lastModified Optional lastModified timestamp to store with the logo
     * @returns Observable<LogoResult>
     */
    private fetchLogoFromServer(userId: number, lastModified: string): Observable<LogoResult> {
        return this.userService.fetchReportLogo().pipe(
            tap((response: LogoGetDto) => {
                const base64 = response?.base64;
                if (base64 && lastModified) {
                    LocalStorageService.storeUserUploadedLogo(base64, userId);
                    LocalStorageService.storeLogoLastModified(lastModified, userId);
                }
            }),
            map((response: LogoGetDto) => {
                const base64 = response?.base64;
                if (base64) {
                    return { logo: base64, source: 'server' as const };
                } else {
                    // No logo found on server, clear cache
                    this.clearCache(userId);
                    return { logo: null, source: 'none' as const };
                }
            }),
            catchError((error) => {
                throw error;
            }),
        );
    }

    /**
     * Uploads a logo file to the server and updates the cache.
     * The backend returns a 204 on success without any body.
     * @param file The logo file to upload
     * @param userId The user ID
     * @returns Observable<string> emitting a base64 encoded string of the
     *          uploaded logo image (JPEG or PNG) on success
     * @throws Error if file type is invalid, file size exceeds 1MB, file reading fails,
     *         or upload to server fails
     */
    uploadLogo(file: File, userId: number): Observable<string> {
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
            return throwError(() => new Error('Invalid file type. Only PNG, JPEG, and JPG are allowed.'));
        }
        // max 1MB file size allowed
        const maxSize = 1000000;
        if (file.size > maxSize) {
            return throwError(() => new Error('File size exceeds 1MB limit.'));
        }

        return new Observable((observer) => {
            const reader = new FileReader();
            reader.onload = (): void => {
                const base64 = reader.result as string;

                this.userService
                    .uploadReportLogo(file)
                    .pipe(
                        tap(() => {
                            LocalStorageService.storeUserUploadedLogo(base64, userId);
                        }),
                        map(() => base64),
                        catchError((error: unknown) => {
                            throw error;
                        }),
                    )
                    .subscribe({
                        next: (result) => {
                            observer.next(result);
                            observer.complete();
                        },
                        error: (error) => {
                            observer.error(error);
                        },
                    });
            };

            reader.onerror = (): void => {
                observer.error(new Error('Failed to read file'));
            };

            reader.readAsDataURL(file);
        });
    }

    /**
     * Deletes the user's uploaded logo from the server and clears the cache.
     * The backend returns a 204 on success without any body.
     * @param userId The user ID
     * @returns Observable<void> indicating success
     */
    deleteLogo(userId: number): Observable<void> {
        return this.userService.deleteReportLogo().pipe(
            tap(() => {
                this.clearCache(userId);
            }),
        );
    }

    /**
     * Clears the logo cache for a specific user.
     * @param userId The user ID
     */
    private clearCache(userId: number): void {
        LocalStorageService.removeUserUploadedLogo(userId);
        LocalStorageService.removeLogoLastModified(userId);
    }

    /**
     * Clears legacy logo storage
     */
    public clearLegacyCache(): void {
        LocalStorageService.clearLegacyLogo();
    }

    /**
     * Returns the default logo as a base64 encoded string (JPEG or PNG).
     * Fetches from local storage first and, if missing, fetches the logo via
     * AssetUtilsService and stores it for subsequent calls.
     */
    public async getDefaultLogo(): Promise<Logo | null> {
        const cached = LocalStorageService.getDefaultLogoBase64();
        if (cached) {
            return cached;
        }
        try {
            return await this.assetUtilsService.defaultLogoToBase64();
        } catch {
            return null;
        }
    }

    public getDefaultLogoObservable(): Observable<Logo | null> {
        return from(this.getDefaultLogo()).pipe(catchError(() => of(null)));
    }
}
